import { Serializable } from '@/models/core/serializable';
import { GEnum } from '@/constants/enum.defination';
import { Constants } from '@/constants/constants';
import moment from 'moment';
import i18n from '@/i18n';
import { EvalSettingMail } from '@/models/evalSetting/eval-setting-mail';

export class EvalSettingSimple implements Serializable<EvalSettingSimple> {
  evalSettingID: number;
  title: string;
  status: GEnum.ExamStatus;
  startTime: Date;
  deadline: Date;
  reportDisplayType: number;
  reportEnabled: boolean;
  selectedReportLink: GEnum.PersonalReportDisplayType; // レポートリンクのドロップダウン選択番号
  questionSettingType: number;
  reportZipStatus: GEnum.ReportStatus; // ZIPフィードバックレポート 生成ステータス
  completedAt: Date;
  isFixed: boolean;
  isExistGCSUS: boolean;
  giftRequired: boolean;
  mailSetting: EvalSettingMail = new EvalSettingMail();

  deserialize(input: any): EvalSettingSimple {
    this.evalSettingID = input.evalSettingID;
    this.title = input.title;
    this.status = input.status;
    if (input.startTime !== undefined) {
      this.startTime = new Date(input.startTime);
    }
    if (input.deadline !== undefined) {
      this.deadline = new Date(input.deadline);
    }
    this.reportDisplayType = input.reportDisplayType;
    this.reportEnabled = input.reportEnabled;
    this.selectedReportLink = this.getSelectedReportLink(this.reportDisplayType, this.reportEnabled);
    this.questionSettingType = input.questionSettingType;
    this.reportZipStatus = input.reportZipStatus;
    this.completedAt = input.completedAt;
    this.isFixed = input.isFixed;
    this.isExistGCSUS = input.isExistGCSUS;
    this.giftRequired = input.giftRequired;
    if (input.mailSetting != undefined) {
      this.mailSetting =  new EvalSettingMail().deserialize(input.mailSetting);
    }

    return this;
  }

  get isNewScoreDay(): boolean {
    return this.deadline > Constants.NewScoreDicisionDate && new Date() > Constants.NewScoreDicisionDate;
  }

  get statusColor(): string {
    switch (this.status) {
      case GEnum.ExamStatus.BeforeStart:
        return 'warning';
      case GEnum.ExamStatus.UnderExamination:
        return 'success';
      case GEnum.ExamStatus.EndOfExamination:
        return 'grey';
      default:
        return '';
    }
  }

  get isBeforeStart(): boolean {
    return this.status === GEnum.ExamStatus.BeforeStart;
  }

  get isBeforeEnd(): boolean {
    return this.status === GEnum.ExamStatus.BeforeStart || this.status === GEnum.ExamStatus.UnderExamination;
  }

  get statusLabel(): string {
    switch (this.status) {
      case GEnum.ExamStatus.BeforeStart:
        return i18n.t('EXAM_STATUS_BEFORE_START').toString();
      case GEnum.ExamStatus.UnderExamination:
        return i18n.t('EXAM_STATUS_IN_PROGRESS').toString();
      case GEnum.ExamStatus.EndOfExamination:
        return i18n.t('EXAM_STATUS_END').toString();
      default:
        return '';
    }
  }

  get reportZipStatusText(): string {
    switch (this.reportZipStatus) {
      case GEnum.ReportStatus.NoRequest:
        return i18n.t('UNGENERATED').toString();
      case GEnum.ReportStatus.Requesting:
        return i18n.t('REQUESTING').toString();
      case GEnum.ReportStatus.Creating:
        return i18n.t('FEEDBACK_REPORT_GENERATING').toString();
      case GEnum.ReportStatus.Completed:
        return i18n.t('FEEDBACK_REPORT_GENERATED').toString();
      case GEnum.ReportStatus.CreateError:
        return i18n.t('FEEDBACK_REPORT_FAILED').toString();
    }
  }

  get reportZipStatusColor(): string {
    switch (this.reportZipStatus) {
      case GEnum.ReportStatus.NoRequest:
        return 'grey';
      case GEnum.ReportStatus.Requesting:
        return 'warning';
      case GEnum.ReportStatus.Creating:
        return 'primary';
      case GEnum.ReportStatus.Completed:
        return 'success';
      case GEnum.ReportStatus.CreateError:
        return 'error';
    }
  }

  get reportZipStatusTooltipContent(): string {
    switch (this.reportZipStatus) {
      case GEnum.ReportStatus.NoRequest:
        return i18n.t('CANNOT_DOWNLOAD_ZIP').toString();
      case GEnum.ReportStatus.Requesting:
        return i18n.t('CANNOT_DOWNLOAD_ZIP').toString();
      case GEnum.ReportStatus.Creating:
        return i18n.t('CANNOT_DOWNLOAD_ZIP').toString();
      case GEnum.ReportStatus.Completed:
        return i18n.t('DOWNLOAD_ZIP').toString();
      case GEnum.ReportStatus.CreateError:
        return i18n.t('CANNOT_DOWNLOAD_ZIP').toString();
    }
  }

  get scoreLabel(): string {
    if (this.isNewScoreDay) {
      return this.isExistGCSUS ? (this.isFixed ? i18n.t('FIXED').toString() : i18n.t('PRELIMINARY').toString()) : '';
    } else {
      // 新スコア判定日以前の場合、一覧にスコアは表示しない
      return '';
    }
  }

  get isReportStatusNoCompleted(): boolean {
    return this.reportZipStatus === GEnum.ReportStatus.NoCompleted;
  }
  get isReportStatusNoRequest(): boolean {
    return this.reportZipStatus === GEnum.ReportStatus.NoRequest;
  }
  get isReportStatusRequesting(): boolean {
    return this.reportZipStatus === GEnum.ReportStatus.Requesting;
  }
  get isReportStatusCreating(): boolean {
    return this.reportZipStatus === GEnum.ReportStatus.Creating;
  }
  get isReportStatusCompleted(): boolean {
    return this.reportZipStatus === GEnum.ReportStatus.Completed;
  }
  get isReportStatusCreateError(): boolean {
    return this.reportZipStatus === GEnum.ReportStatus.CreateError;
  }

  /**
   * reportEnabled(レポート表示可否)が true(表示する)場合は reportDisplayType を返却する。
   * false(表示しない)の場合は GEnum.PersonalReportDisplayType.NoShow を返却する。
   * @param reportDisplayType
   * @param reportEnabled
   * @returns
   */
  getSelectedReportLink(reportDisplayType: number, reportEnabled: boolean): GEnum.PersonalReportDisplayType {
    return reportEnabled ? reportDisplayType : GEnum.PersonalReportDisplayType.NoShow;
  }
}

export interface UpdateEvalSettingSimpleRequest {
  evalSettingID: number;
  title: string;
  startTime: Date;
  deadline: Date;
}
